import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import {
  Alert,
  Button,
  Divider,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form941Instructions2023 = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="IRS Form 941 2023: Instructions, Deadlines & Filing Methods"
          description="Learn how to complete IRS Form 941 for 2023, a quarterly federal tax return, with deadlines, instructions, penalties, and e-filing via TaxZerone."
          keywords="Form 941, Payroll tax filing, IRS Form 941, Employment tax, Quarterly tax reporting"
          canonical="resources/employment-tax/form-941-instructions-2023/"
          ogType="article"
          jsonLdPricing="6.99"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="54869"
        />
      }
    >
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form 941</span>
          </span>{' '}
          Instructions 2023
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/employment-tax/form-941-instructions-2023/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-06-15T09:00:00-05:00"
                      >
                        June 15, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-06-15T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        June 15, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Nikhil Chowdary
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 941 for 2023 at a Glance
                  </Typography>
                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <b>Form 941</b>
                        {' is the '}
                        <b>Employer's Quarterly Federal Tax Return</b>
                        {
                          ' used by employers to report employee wages, federal income tax withheld, Social Security and Medicare wages and taxes, and other employment tax information to the IRS.'
                        }
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        <b>Who Must File:</b>
                        {
                          ' Employers who pay wages subject to federal income tax withholding or Social Security and Medicare taxes generally must file Form 941. Employers specifically notified by the IRS to file Form 944 generally do not file Form 941.'
                        }
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        <b>When to File:</b>
                        {
                          ' Form 941 is filed quarterly. The regular deadlines are '
                        }
                        <b>April 30, July 31, October 31, and January 31</b>
                        {
                          '. If a due date falls on a weekend or federal holiday, the return is generally '
                        }
                        due on
                        {' the next business day.'}
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        <b>Penalties:</b>
                        <b> </b>
                        Penalties may be imposed for failure to file, failure to
                        pay taxes, or late filing/payment of taxes
                        {'. '}{' '}
                      </span>
                    </li>
                  </Box>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What is Form 941?
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  Federal{' '}
                  <Link
                    href="/resources/employment-tax/form-941-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 941
                  </Link>{' '}
                  is the Employer&apos;s Quarterly Federal Tax Return form used
                  by employers to report wages paid to employees and federal
                  payroll taxes. This form is filed quarterly to report:
                </Typography>
                <List sx={{ mb: 1 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>Total wages paid</ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>Federal income tax withheld</ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Social Security and Medicare wages and taxes
                    </ListItemText>
                  </ListItem>
                </List>

                <Typography variant="body1" sx={{ mb: 4 }}>
                  Form 941 is filed by employers who pay wages subject to income
                  tax withholding or Social Security and Medicare taxes. It is
                  used to determine the employer&apos;s tax liability for the
                  quarter.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Who can file Form 941?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Every employer who pays wages to employees that subject to
                  income tax withholding or Social Security and Medicare taxes
                  must{' '}
                  <Link
                    href="/employment-tax/e-file-irs-form-941/"
                    sx={{ color: '#0202ff' }}
                  >
                    file Form 941
                  </Link>{' '}
                  with the IRS.
                </Typography>

                <Typography variant="body1" sx={{ mb: 4 }}>
                  However, if the IRS has notified your business to{' '}
                  <Link
                    href="/employment-tax/e-file-irs-form-944/"
                    sx={{ color: '#0202ff' }}
                  >
                    file Form 944
                  </Link>
                  , then you are not required to <br />
                  <Link
                    href="/employment-tax/e-file-irs-form-941/"
                    sx={{ color: '#0202ff' }}
                  >
                    file Form 941
                  </Link>
                  .
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  When is the Form 941 due date?
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Form 941 needs to be filed quarterly. Below are the deadlines
                  for{' '}
                  <Link
                    href="/employment-tax/e-file-irs-form-941/"
                    sx={{ color: '#0202ff' }}
                  >
                    filing Form 941
                  </Link>{' '}
                  for each quarter.
                </Typography>
                <Box component="div" sx={{ mb: 2 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Quarter
                          </TableCell>

                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Deadline
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>Quarter 1 (January–March)</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            April 30
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Quarter 2 (April–June)</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            July 31
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>Quarter 3 (July–September)</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            October 31
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Quarter 4 (October–December)</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            January 31
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Alert severity="warning" icon={false} sx={{ mb: 4 }}>
                  <b>Note:</b> If the due date falls on a Saturday, Sunday, or
                  federal holiday, Form 941 can be filed the next business day.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Form 941 instructions - How to fill out?
                </Typography>

                <Typography variant="body1">
                  Follow these 2023 941 instructions to complete your employers
                  quarterly filing accurately.
                </Typography>
                <Box
                  component="img"
                  src="/assets/images/941-instruction-img-employer-details.webp"
                  alt="Enter EIN, Name, complete address and select the Quarter"
                  sx={{ width: '100%', border: '1px solid #333' }}
                />

                <List sx={{ mb: 3 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Enter your business name, EIN, and address.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Check the quarter box for which you’re filing the return.
                    </ListItemText>
                  </ListItem>
                </List>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  PART 1
                </Typography>

                <Box
                  component="img"
                  src="/assets/images/941-instruction-img-part1.webp"
                  alt="2023 Form 941 Part 1 showing employee count, wages, and federal tax withheld"
                  sx={{ width: '100%', border: '1px solid #333', mb: 1 }}
                />
                <Typography variant="body1">
                  <b>
                    <i>Line 1</i>:
                  </b>{' '}
                  Enter the number of employees who received wages, tips, or
                  other compensation during the pay period. Do not count these
                  workers:
                </Typography>
                <Box component="ul" sx={{ mb: 4 }}>
                  <li>Household employees</li>
                  <li>Employees who did not receive pay for the period</li>
                  <li>Farm employees</li>
                  <li>Retirees receiving pensions</li>
                  <li>Active members of the U.S. Armed Forces</li>
                </Box>
                <Typography variant="body1">
                  <b>
                    <i>Line 2</i>:
                  </b>{' '}
                  Enter the total wages, tips, and other compensation that would
                  be reported in box 1 of your employees&apos; Forms W-2. This
                  includes:
                </Typography>
                <Box component="ul" sx={{ mb: 2 }}>
                  <li>Regular wages</li>
                  <li>Tips</li>
                  <li>Sick pay paid by you or your agent</li>
                  <li>
                    Sick pay paid by a third party if they notified you and
                    transferred tax liability to you.
                  </li>
                </Box>
                <Alert severity="warning" icon={false} sx={{ mb: 4 }}>
                  <b>DO NOT</b> include sick pay you paid as a third party payer
                  (not an agent of the employer) if you notified the employer
                  about the payments.
                </Alert>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>
                    <i>Line 3</i>:
                  </b>{' '}
                  Enter the total wages, tips, and other compensation that would
                  be reported in box 1 of your employees&apos; Forms W-2. This
                  includes:
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  If you&apos;re a third-party payer of sick pay, enter federal
                  income tax withheld on third-party sick pay here.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>
                    <i>Line 4</i>:
                  </b>{' '}
                  If none of the wages, tips, and other compensation on line 2
                  are subject to Social Security or Medicare tax, check the box
                  on line 4. If they are subject to these taxes, leave the box
                  blank.
                </Typography>
                <Divider sx={{ mb: 3, backgroundColor: '#ccc' }} />
                <Box
                  component="img"
                  src="/assets/images/941-instruction-img-line5a.webp"
                  alt="2023 Form 941 tax calculation section showing Social Security and Medicare wages, tips, and tax rates."
                  sx={{ width: '100%', border: '1px solid #333', mb: 1 }}
                />
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>
                    <i>Line 5a - Taxable social security wages</i>:
                  </b>{' '}
                  Enter the total wages, including qualified sick leave and
                  family leave wages paid in the tax year, sick pay, and taxable
                  fringe benefits subject to Social Security tax that you paid
                  this quarter. Sick pay includes insurance company payments to
                  employees which you were notified of.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Enter the amount before payroll deductions, excluding tips.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>
                    <i>Line 5a(i) - Qualified sick leave wages</i>:
                  </b>{' '}
                  Enter the qualified taxable sick leave wages paid this quarter
                  in 2023 for leave taken 3/31/20 – 3/31/21. These wages
                  aren&apos;t subject to the employer&apos;s share of Social
                  Security tax (6.2% rate).
                </Typography>
                <Alert severity="warning" icon={false} sx={{ mb: 4 }}>
                  <b>Note:</b> Stop reporting these wages on line 5a(i) when an
                  employee&apos;s total taxable wages, sick leave wages, family
                  leave wages, and tips reach the $160,200 Social Security wage
                  base.
                </Alert>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>
                    <i>Line 5a(ii) - Qualified family leave wages</i>:
                  </b>{' '}
                  Enter the qualified taxable family leave wages paid this
                  quarter in 2023 for leave taken 3/31/20 – 3/31/21. These wages
                  aren&apos;t subject to the employer&apos;s share of Social
                  Security tax (6.2% rate).
                </Typography>
                <Alert severity="warning" icon={false} sx={{ mb: 4 }}>
                  <b>Note:</b> Stop reporting these wages on line 5a(ii) when an
                  employee&apos;s total taxable wages, sick leave wages, family
                  leave wages, and tips reach the $160,200 Social Security wage
                  base.
                </Alert>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  <b>
                    <i>Line 5b - Taxable Social Security tips</i>:
                  </b>{' '}
                  Enter all tips employees reported this quarter until the total
                  tips and taxable wages, including wages on line 5a, sick leave
                  wages on 5a(i), and family leave wages on 5a(ii), reach
                  $160,200 per employee for the year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>
                    <i>Line 5c - Taxable Medicare wages & tips</i>:
                  </b>{' '}
                  Enter all wages, including qualified sick leave and family
                  leave wages paid this quarter in 2023, tips, sick pay, and
                  taxable fringe benefits subject to Medicare tax.
                </Typography>
                <Alert severity="warning" icon={false} sx={{ mb: 4 }}>
                  <b>Note:</b> There is no wage base limit for Medicare tax.
                </Alert>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  <b>
                    <i>
                      Line 5d - Wages & tips subject to Additional Medicare Tax
                    </i>
                    :
                  </b>{' '}
                  Enter all wages, including qualified sick leave and family
                  leave wages paid this quarter in 2023, tips, sick pay, and
                  taxable fringe benefits subject to Additional Medicare Tax
                  withholding.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  <b>
                    <i>Line 5e - Total social security and Medicare taxes</i>:
                  </b>{' '}
                  Add the column 2 amounts on lines 5a–5d, and enter the result
                  amount.
                </Typography>
                <Box
                  component="div"
                  sx={{ mb: 5, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          mb: 1,
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <TipsAndUpdatesIcon sx={{ mr: 1, color: '#ffe400' }} />{' '}
                        Tip
                      </Typography>
                      <Typography variant="body2">
                        If you e-file Form 941 using TaxZerone, these
                        calculations will be automatically performed. You only
                        need to provide the information.
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 170, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=941instructions2023`}
                      >
                        E-file Form 941
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>
                    <i>
                      Line 5f. Section 3121(q) notice and demand—tax due on
                      unreported tips
                    </i>
                    :
                  </b>{' '}
                  Enter the tax due from your Section 3121(q) Notice and Demand
                  for unreported or underreported tips. The IRS issues this
                  notice to advise employers of the amount of tips received by
                  employees who failed to fully report tips.
                </Typography>
                <Divider sx={{ mb: 3, backgroundColor: '#ccc' }} />
                <Box
                  component="img"
                  src="/assets/images/941-instruction-img-line6.webp"
                  alt="2023 Form 941 adjustment section showing total taxes, quarterly adjustments, and payroll tax credits."
                  sx={{ width: '100%', border: '1px solid #333', mb: 1 }}
                />
                <Typography variant="body1">
                  <b>
                    <i>Line 6 - Total taxes before adjustments</i>:
                  </b>{' '}
                  Add these values together:
                </Typography>
                <Box component="ul" sx={{ mb: 4 }}>
                  <li>Federal income tax withheld (line 3)</li>
                  <li>
                    Total Social Security and Medicare taxes before adjustments
                    (line 5e)
                  </li>
                  <li>
                    Any tax due under Section 3121(q) Notice and Demand (line
                    5f) Enter the total on line 6.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  <b>
                    <i>Line 7 - Adjustment for rounding</i>:
                  </b>{' '}
                  Enter adjustments for fractions of cents (due to rounding)
                  related to the employee share of Social Security and Medicare
                  taxes withheld. The employee share amounts on lines 5a–5d may
                  differ slightly from actual withholding due to rounding based
                  on statutory rates. This adjustment can be positive or
                  negative.
                </Typography>
                <Typography variant="body1">
                  <b>
                    <i>
                      Line 8 - Current quarter&apos;s adjustment for sick pay
                    </i>
                    :
                  </b>{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 2 }}>
                  <li>
                    If a third-party payer of sick pay (not your agent)
                    transferred the employer share of Social Security and
                    Medicare taxes to you, enter a negative adjustment for the
                    employee share they withheld and deposited.
                  </li>
                  <li>
                    If you&apos;re the third-party payer and transferred the
                    employer share to the employer, enter a negative adjustment
                    for any employer share the employer must pay.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Include the sick pay on lines 5a, 5c, and 5d if the
                  withholding threshold is met.
                </Typography>
                <Typography variant="body1">
                  <b>
                    <i>
                      Line 9 - Current quarter&apos;s adjustments for tips and
                      group-term life insurance
                    </i>
                    :
                  </b>{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 4 }}>
                  <li>
                    Enter a negative adjustment for any uncollected employee
                    share of Social Security and Medicare taxes on tips.
                  </li>
                  <li>
                    Enter a negative adjustment for any uncollected employee
                    share of Social Security and Medicare taxes on group-term
                    life insurance premiums paid for former employees.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  <b>
                    <i>Line 10 - Total taxes after adjustments</i>:
                  </b>{' '}
                  Add the amounts shown on lines 6–9 and enter the result.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  <b>
                    <i>
                      Line 11a - Qualified small business payroll tax credit for
                      increasing research activities
                    </i>
                    :
                  </b>{' '}
                  Enter the qualified small business payroll tax credit for
                  increasing research activities from Form 8974, line 12 or line
                  17 if applicable.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>
                    <i>
                      Line 11b - Nonrefundable portion of credit for qualified
                      sick and family leave wages for leave taken after March
                      31, 2020, and before April 1, 2021
                    </i>
                    :
                  </b>{' '}
                  Enter the nonrefundable portion of the credit for qualified
                  sick and family leave wages paid in 2023 for leave taken
                  3/31/20 – 3/31/21. Calculate the nonrefundable portion using
                  Worksheet 1, Step 2, line 2j.
                </Typography>
                <Divider sx={{ mb: 3, backgroundColor: '#ccc' }} />
                <Box
                  component="img"
                  src="/assets/images/941-instruction-img-line11d.webp"
                  alt="IRS 2023 Form 941 nonrefundable credit section for qualified sick and family leave wages "
                  sx={{ width: '100%', border: '1px solid #333', mb: 1 }}
                />
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>
                    <i>
                      Line 11d - Nonrefundable portion of credit for qualified
                      sick and family leave wages for leave taken after March
                      31, 2021, and before October 1, 2021
                    </i>
                    :
                  </b>{' '}
                  Enter the nonrefundable portion of the credit for qualified
                  sick and family leave wages paid this quarter of 2023 for
                  leave taken 3/31/21 – 9/30/21. Calculate the nonrefundable
                  portion using Worksheet 2, Step 2, line 2p.
                </Typography>
                <Alert severity="warning" icon={false} sx={{ mb: 4 }}>
                  <b>Note:</b> The nonrefundable portion is limited to the
                  employer share of Social Security tax after reducing that
                  share by other credits claimed.
                </Alert>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>
                    <i>Line 11g - Total nonrefundable credits</i>:
                  </b>{' '}
                  Add lines 11a, 11b, and 11d and enter the total.
                </Typography>
                <Divider sx={{ mb: 3, backgroundColor: '#ccc' }} />
                <Box
                  component="img"
                  src="/assets/images/941-instruction-img-line12.webp"
                  alt="IRS 2023 Form 941 section showing total deposits, refundable credits, balance due, and overpayment."
                  sx={{ width: '100%', border: '1px solid #333', mb: 1 }}
                />
                <Typography variant="body1">
                  <b>
                    <i>
                      Line 12 - Total taxes after adjustments and nonrefundable
                      credits
                    </i>
                    :
                  </b>{' '}
                  Subtract line 11g from line 10 and enter the result on line
                  12. The amount can&apos;t be less than zero.
                </Typography>
                <Box component="ul" sx={{ mb: 2 }}>
                  <li>
                    If line 12 is under $2,500 or line 12 on the prior return
                    was under $2,500, and you didn&apos;t incur a $100,000
                    next-day deposit obligation this quarter, you may pay with
                    Form 941 or deposit the amount. To avoid a penalty, pay in
                    full with a timely filed return or deposit the amount before
                    the due date.
                  </li>
                  <li>
                    If line 12 is $2,500 or more and line 12 on the prior return
                    was $2,500 or more, or you incurred a $100,000 next-day
                    deposit obligation this quarter, you must make required
                    deposits according to your schedule.
                  </li>
                </Box>
                <Alert severity="warning" icon={false} sx={{ mb: 4 }}>
                  <b>Note:</b> This amount must equal the total liability on
                  line 16 or Schedule B, line 16.
                </Alert>
                <Typography variant="body1">
                  <b>
                    <i>Line 13a - Total deposits for this quarter</i>:
                  </b>{' '}
                  Enter your total tax deposits for this quarter, including:
                </Typography>
                <Box component="ul" sx={{ mb: 2 }}>
                  <li>
                    Any overpayment from a prior quarter that you applied to
                    this return
                  </li>
                  <li>
                    Any overpayment from Form 941-X, 944-X, etc., that you
                    applied in the current quarter
                  </li>
                </Box>
                <Alert severity="warning" icon={false} sx={{ mb: 4 }}>
                  <b>DO NOT</b> include any deposit amount you reduced in
                  anticipation of credits for qualified sick and family leave
                  wages per Notice 2020-22 and Notice 2021-24.
                </Alert>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>
                    <i>
                      Line 13c - Refundable portion of credit for qualified sick
                      and family leave wages for leave taken after March 31,
                      2020, and before April 1, 2021
                    </i>
                    :
                  </b>{' '}
                  Enter the refundable portion of the credit from Worksheet 1,
                  Step 2, line 2k.
                </Typography>
                <Alert severity="warning" icon={false} sx={{ mb: 4 }}>
                  <b>Note:</b> Complete this line only if you paid qualified
                  sick and family leave wages this quarter in 2023 for leave
                  taken 3/31/20 – 3/31/21.
                </Alert>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>
                    <i>
                      Line 13e - Refundable portion of credit for qualified sick
                      and family leave wages for leave taken after March 31,
                      2021, and before October 1, 2021
                    </i>
                    :
                  </b>{' '}
                  Enter the refundable portion of the credit from Worksheet 2,
                  Step 2, line 2q.
                </Typography>
                <Alert severity="warning" icon={false} sx={{ mb: 4 }}>
                  <b>Note:</b> Complete this line only if you paid qualified
                  sick and family leave wages this quarter in 2023 for leave
                  taken 3/31/21 – 9/30/21.
                </Alert>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  <b>
                    <i>Line 13g - Total deposits and refundable credits</i>:
                  </b>{' '}
                  Add lines 13a, 13c, and 13e and enter the total.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  <b>
                    <i>Line 14 - Balance due</i>:
                  </b>{' '}
                  If line 12 value is more than line 13g, enter the difference
                  on line 14.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>
                    <i>Line 15 - Overpayment</i>:
                  </b>{' '}
                  If line 13g is more than line 12, enter the difference on line
                  15.
                </Typography>
                <Alert severity="warning" icon={false} sx={{ mb: 4 }}>
                  <b>DO NOT</b> fill out both lines 14 and 15. It should be
                  either one.
                </Alert>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  PART 2
                </Typography>

                <Box
                  component="img"
                  src="/assets/images/941-instruction-img-part2.webp"
                  alt="2023 Form 941 Part 2 showing deposit schedule and quarterly tax liability details."
                  sx={{ width: '100%', border: '1px solid #333', mb: 1 }}
                />
                <Typography variant="body1">
                  <b>
                    <i>Line 16 - Tax liability for the quarter</i>:
                  </b>{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 4 }}>
                  <li>
                    If line 12 is under $2,500 or line 12 on the prior return
                    was under $2,500, and you didn&apos;t have a $100,000
                    next-day deposit this quarter, check the first box and go to
                    Part 3.{' '}
                  </li>
                  <li>
                    If you reported $50,000 or less in taxes in the lookback
                    period, you are a monthly schedule depositor unless the
                    $100,000 next-day deposit rule applies. Check the second box
                    and enter your monthly tax liabilities for when wages were
                    paid, not deposits made. Add the monthly amounts and enter
                    the total liability for the quarter.
                  </li>
                  <li>
                    If you reported over $50,000 in taxes in the lookback
                    period, you are a semiweekly schedule depositor. Check the
                    third box.
                  </li>
                </Box>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  PART 3
                </Typography>

                <Box
                  component="img"
                  src="/assets/images/941-instruction-img-part3.webp"
                  alt="2023 IRS Form 941 Part 3 showing business information, qualified sick and family leave wages."
                  sx={{ width: '100%', border: '1px solid #333', mb: 1 }}
                />
                <Typography variant="body1" sx={{ mb: 4 }}>
                  <b>
                    <i>Line 17 - If your business has closed</i>:
                  </b>{' '}
                  If you close your business or stop paying wages, you need to
                  file a final tax return. To let the IRS know this is your last
                  return, simply check the box on line 17 and enter the date you
                  stopped paying wages.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>
                    <i>Line 18 - If you’re a seasonal employer</i>:
                  </b>{' '}
                  If you hire employees only during certain seasons, check the
                  box on line 18.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  This lets the IRS know that you won&apos;t be submitting four
                  Forms 941 throughout the year because you don&apos;t pay wages
                  regularly.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  <b>
                    <i>
                      Line 19 - Qualified health plan expenses allocable to
                      qualified sick leave wages for leave taken after March 31,
                      2020, and before April 1, 2021
                    </i>
                    :
                  </b>{' '}
                  Enter the qualified health plan expenses allocable to
                  qualified sick leave wages paid this quarter of 2023 for leave
                  taken 3/31/21 – 9/30/21. This amount is also included in Step
                  2, line 2b of Worksheet 1.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  <b>
                    <i>
                      Line 20 - Qualified health plan expenses allocable to
                      qualified family leave wages for leave taken after March
                      31, 2020, and before April 1, 2021
                    </i>
                    :
                  </b>{' '}
                  Enter the qualified health plan costs associated with paid
                  family leave taken for leave taken 3/31/21 – 9/30/21 for this
                  quarter of 2023. This amount is also included in Step 2, line
                  2f of Worksheet 1.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>
                    <i>
                      Line 23 - Qualified sick leave wages for leave taken after
                      March 31, 2021, and before October 1, 2021
                    </i>
                    :
                  </b>{' '}
                  Enter the qualified sick leave wages paid this quarter of 2023
                  for leave taken 3/31/21 – 9/30/21, including any qualified
                  sick leave wages that were above the social security wage base
                  and any qualified sick leave wages excluded from the
                  definition of employment under sections 3121(b)(1)–(22).
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  This amount is also included in Worksheet 2, Step 2, line 2a.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  <b>
                    <i>
                      Line 24. Qualified health plan expenses allocable to
                      qualified sick leave wages reported on line 23
                    </i>
                    :
                  </b>{' '}
                  Enter the qualified health plan expenses associated with
                  qualified sick leave wages paid this quarter of 2023 for leave
                  taken 3/31/21 – 9/30/21. This amount is also included in
                  Worksheet 2, Step 2, line 2b.
                </Typography>

                <Typography variant="body1" sx={{ mb: 4 }}>
                  <b>
                    <i>
                      Line 25 - Amounts under certain collectively bargained
                      agreements allocable to qualified sick leave wages
                      reported on line 23
                    </i>
                    :
                  </b>{' '}
                  Enter contributions to collectively bargained pension plans
                  and apprenticeship programs that are linked to qualified sick
                  leave wages paid this quarter of 2023 for leave taken 3/31/21
                  – 9/30/21. This amount is also included in Worksheet 2, Step
                  2, line 2c
                </Typography>

                <Typography variant="body1" sx={{ mb: 4 }}>
                  <b>
                    <i>
                      Line 26. Qualified Family Leave Wages for Leave Taken
                      After March 31, 2021, and Before October 1, 2021
                    </i>
                    :
                  </b>{' '}
                  Enter the qualified family leave wages paid this quarter of
                  2023 for leave taken 3/31/21 – 9/30/21, including any
                  qualified family leave wages that were above the social
                  security wage base and any qualified family leave wages
                  excluded from the definition of employment under sections
                  3121(b)(1)–(22). This amount is also included in Worksheet 2,
                  Step 2, line 2g.
                </Typography>

                <Typography variant="body1" sx={{ mb: 4 }}>
                  <b>
                    <i>
                      Line 27 - Qualified health plan expenses allocable to
                      qualified family leave wages reported on line 26
                    </i>
                    :
                  </b>{' '}
                  Enter the qualified health plan expenses associated with
                  qualified family leave wages paid this quarter of 2023 for
                  leave taken 3/31/21 – 9/30/21. This amount is also included in
                  Worksheet 2, Step 2, line 2h.
                </Typography>

                <Typography variant="body1" sx={{ mb: 4 }}>
                  <b>
                    <i>
                      Line 28 - Amounts under certain collectively bargained
                      agreements allocable to qualified family leave wages
                      reported on line 26
                    </i>
                    :
                  </b>{' '}
                  Enter contributions to collectively bargained pension plans
                  and apprenticeship programs that are linked to qualified
                  family leave wages paid this quarter of 2023 for leave taken
                  3/31/21 – 9/30/21. This amount is also included in Worksheet
                  2, Step 2, line 2i.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  PART 4: Third-party designee
                </Typography>
                <Box
                  component="img"
                  src="/assets/images/941-instruction-img-part4.webp"
                  alt="2023 IRS Form 941 Part 4 showing third-party designee authorization and PIN section."
                  sx={{ width: '100%', border: '1px solid #333', mb: 1 }}
                />
                <Typography variant="body1" sx={{ mb: 4 }}>
                  If you want to assign a third-party designee to discuss your
                  Form 941 with the IRS on your behalf, check the Yes box and
                  enter their details. Else, you can check the No box.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  PART 5: Sign
                </Typography>
                <Box
                  component="img"
                  src="/assets/images/941-instruction-img-part5.webp"
                  alt="2023 IRS Form 941 Part 5 signature section"
                  sx={{ width: '100%', border: '1px solid #333', mb: 1 }}
                />
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Sign Form 941, provide your name, title, and daytime phone
                  number.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Form 941 penalty
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Penalties may be imposed for failure to file, failure to pay
                  taxes due, or late filing/payment of taxes. The penalties are
                  calculated as a percentage of the tax amount due. Some common
                  Form 941 penalties include:
                </Typography>
                <Box component="div" sx={{ mb: 4 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell sx={{ color: '#444', width: 400 }}>
                            Penalty type
                          </TableCell>

                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Rate
                          </TableCell>
                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Applies to
                          </TableCell>
                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Maximum
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {/* <TableRow sx={{}}>
                          <TableCell>Quarter 1 (January–March)</TableCell>
                          <TableCell>5% per month (up to 5 months)</TableCell>
                          <TableCell>Unpaid tax amount</TableCell>
                          <TableCell>25% of unpaid tax</TableCell>
                        </TableRow> */}
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>
                            Failing to file (more than 60 days late)
                          </TableCell>
                          <TableCell>
                            Lesser of $435 or 100% of tax due
                          </TableCell>
                          <TableCell>Total tax due </TableCell>
                          <TableCell>NA</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>
                            Failing to deposit taxes on time{' '}
                          </TableCell>
                          <TableCell>2%</TableCell>
                          <TableCell>1 to 5 days late</TableCell>
                          <TableCell>NA</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>
                            Failing to deposit taxes on time{' '}
                          </TableCell>
                          <TableCell>5%</TableCell>
                          <TableCell>6 to 15 days late</TableCell>
                          <TableCell>NA</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>
                            Failing to deposit taxes on time{' '}
                          </TableCell>
                          <TableCell>10%</TableCell>
                          <TableCell>
                            16 days late or within 10 days of IRS notice
                          </TableCell>
                          <TableCell>NA</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>
                            Failing to deposit taxes on time{' '}
                          </TableCell>
                          <TableCell>15%</TableCell>
                          <TableCell>
                            More than 10 days after IRS notice
                          </TableCell>
                          <TableCell>NA</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How to file IRS Form 941?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  IRS Form 941 can be filed electronically or by paper:
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Form 941 E-filing
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <Link
                    href="/employment-tax/e-file-irs-form-941/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    E-filing Form 941
                  </Link>{' '}
                  is fast, accurate, and secure. Below are some of the benefits
                  of e-filing:
                </Typography>
                <Box component="ul" sx={{ mb: 2 }}>
                  <li>Automatic calculation of taxes and penalties</li>
                  <li>Instant confirmation that the return is accepted</li>
                  <li>Smooth and efficient e-filing experience</li>
                </Box>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  You can e-file using an IRS-authorized e-file service provider
                  like TaxZerone.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Form 941 paper filing
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you’re choosing the traditional paper filing method, follow
                  the steps below:
                </Typography>
                <Box component="ol" sx={{ mb: 4 }}>
                  <li>Download or order Form 941 from IRS.gov</li>
                  <li>Complete all required information on the form</li>
                  <li>
                    Mail the completed Form 941 to the applicable IRS address
                    below.
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Where to send Form 941 - Mailing address
                </Typography>
                <Box component="div" sx={{ mb: 1 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell sx={{ color: '#444', width: 300 }}>
                            If you’re in...
                          </TableCell>

                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Mail return without payment...
                          </TableCell>
                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Mail return with payment...
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell sx={{ verticalAlign: 'top' }}>
                            Connecticut, Delaware, District of Columbia,
                            Georgia, Illinois, Indiana, Kentucky, Maine,
                            Maryland, Massachusetts, Michigan, New Hampshire,
                            New Jersey, New York, North Carolina, Ohio,
                            Pennsylvania, Rhode Island, South Carolina,
                            Tennessee, Vermont, Virginia, West Virginia,
                            Wisconsin{' '}
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'top' }}>
                            <b>Department of the Treasury</b>
                            <br />{' '}
                            <span style={{ fontWeight: 600 }}>
                              Internal Revenue Service
                            </span>
                            <br /> Kansas City, MO 64999-0005
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'top' }}>
                            <span style={{ fontWeight: 600 }}>
                              Internal Revenue Service
                            </span>
                            <br /> P.O. Box 806532
                            <br /> Cincinnati, OH 45280-6532
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ verticalAlign: 'top' }}>
                            Alabama, Alaska, Arizona, Arkansas, California,
                            Colorado, Florida, Hawaii, Idaho, Iowa, Kansas,
                            Louisiana, Minnesota, Mississippi, Missouri,
                            Montana, Nebraska, Nevada, New Mexico, North Dakota,
                            Oklahoma, Oregon, South Dakota, Texas, Utah,
                            Washington, Wyoming{' '}
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'top' }}>
                            <b>Department of the Treasury</b>
                            <br />{' '}
                            <span style={{ fontWeight: 600 }}>
                              Internal Revenue Service
                            </span>
                            <br /> Ogden, UT 84201-0005
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'top' }}>
                            <span style={{ fontWeight: 600 }}>
                              Internal Revenue Service
                            </span>
                            <br /> P.O. Box 932100
                            <br />
                            Louisville, KY 40293-2100
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell sx={{ verticalAlign: 'top' }}>
                            No legal residence or principal place of business in
                            any state:{' '}
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'top' }}>
                            <span style={{ fontWeight: 600 }}>
                              Internal Revenue Service
                            </span>
                            <br /> P.O. Box 409101
                            <br />
                            Ogden, UT 84409
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'top' }}>
                            <span style={{ fontWeight: 600 }}>
                              Internal Revenue Service
                            </span>
                            <br /> P.O. Box 932100
                            <br />
                            Louisville, KY 40293-2100
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ verticalAlign: 'top' }}>
                            Special filing address for exempt organizations;
                            governmental entities; and Indian tribal
                            governmental entities; regardless of location{' '}
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'top' }}>
                            <b>Department of the Treasury</b>
                            <br />{' '}
                            <span style={{ fontWeight: 600 }}>
                              Internal Revenue Service
                            </span>
                            <br /> Ogden, UT 84201-0005
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'top' }}>
                            <span style={{ fontWeight: 600 }}>
                              Internal Revenue Service
                            </span>
                            <br /> P.O. Box 932100
                            <br />
                            Louisville, KY 40293-2100
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography variant="body2" sx={{ mb: 4 }}>
                  Source:{' '}
                  <Link
                    href="https://www.irs.gov/filing/where-to-file-your-taxes-for-form-941"
                    target="_blank"
                    sx={{ color: '#0202ff' }}
                  >
                    IRS
                  </Link>
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How to e-file Form 941 using TaxZerone?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  E-filing your Form 941 takes just 3 steps. To make it even
                  faster, gather all the necessary information before you start.
                  That way, you can zip through the filing process in no time.
                </Typography>
                <Box component="ol" sx={{ mb: 4 }}>
                  <li style={{ marginBottom: 16 }}>
                    <b>Enter Form 941 information</b>
                    <br />
                    Provide all necessary details including employer and
                    employment information, tax liabilities, federal income
                    taxes, Social Security and Medicare taxes, and total
                    deposits and payments made for the quarter.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Pay the balance due and sign Form 941</b>
                    <br />
                    Select your preferred payment method to clear any
                    outstanding balance, e-sign the return, and carefully review
                    all information on the form to confirm its accuracy.
                  </li>
                  <li style={{}}>
                    <b>Transmit the return to the IRS</b>
                    <br />
                    Securely transmit your completed Form 941 to the IRS and
                    receive prompt notification once your return has been
                    accepted.
                  </li>
                </Box>
                <Box
                  component="div"
                  sx={{
                    mb: 0,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    backgroundSize: {
                      xs: '20% auto',
                      sm: '20% auto',
                      md: '20% auto',
                    },
                    border: '1px solid #ccc',
                    // color: '#fff',
                  }}
                  // className="wave-bg"
                >
                  <Box sx={{ width: '100%' }}>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 600,
                        mb: 1,
                        fontSize: '24px !important',
                      }}
                    >
                      Do you have the required information, and do you find the
                      steps simple?
                    </Typography>
                    <Typography variant="body2">
                      The whole filing process gets completed in less than 5
                      minutes! You will also get email updates on your return
                      status as soon as the IRS processes it.
                    </Typography>
                  </Box>
                  <Box sx={{ width: '100%', textAlign: 'left', mt: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{
                        textTransform: 'none',
                        boxShadow: 'none',
                        borderRadius: 2,
                      }}
                      size="large"
                      href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=941instructions2023`}
                    >
                      E-File Form 941 Now
                    </Button>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
              '& li': { fontSize: 16 },
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Why should you e-file Form 941 with TaxZerone?
            </Typography>
            <List sx={{ mb: 3, textAlign: 'left', maxWidth: 750, mx: 'auto' }}>
              <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                  <DoubleArrowIcon />
                </ListItemIcon>
                <ListItemText>
                  IRS-authorized e-file service provider
                </ListItemText>
              </ListItem>
              <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                  <DoubleArrowIcon />
                </ListItemIcon>
                <ListItemText>
                  Smart IRS validations to reduce errors in the return
                </ListItemText>
              </ListItem>
              <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                  <DoubleArrowIcon />
                </ListItemIcon>
                <ListItemText>
                  Supports{' '}
                  <Link
                    href="/resources/employment-tax/form-941-schedule-b-filing-requirements/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Schedule B
                  </Link>{' '}
                  &{' '}
                  <Link
                    href="/resources/employment-tax/form-8974-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 8974
                  </Link>{' '}
                  to report any additional tax liabilities and tax credit
                  amounts accurately
                </ListItemText>
              </ListItem>
            </List>

            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 4,
              }}
            >
              Simplify your quarterly payroll tax filing with TaxZerone at just{' '}
              <b>$6.99 per return</b>.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=941instructions2023`}
              >
                E-file Form 941 Now
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form941Instructions2023;
